import { useEffect, useState } from 'react';

export const useGooglePlacesAutocomplete = (show) => {
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [addressPredictions, setAddressPredictions] = useState([]);
    const [autocompleteService, setAutocompleteService] = useState(null);
    const [placesService, setPlacesService] = useState(null);

    useEffect(() => {
        if (show) {
            loadGoogleMapsScript(() => {
                if (window.google && window.google.maps && window.google.maps.places) {
                    setAutocompleteService(new window.google.maps.places.AutocompleteService());
                    setPlacesService(new window.google.maps.places.PlacesService(document.createElement('div')));
                }
            });
        }
    }, [show]);

    const loadGoogleMapsScript = (callback) => {
        if (window.google && window.google.maps && window.google.maps.places) {
            callback();
            return;
        }

        const script = document.createElement('script');
        //TODO: Change API key from mgmt hub instance to bizismart instance
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
            if (window.google && window.google.maps && window.google.maps.places) {
                callback();
            } else {
                console.error("Google Maps script loaded, but 'places' library is unavailable.");
            }
        };

        script.onerror = () => {
            console.error("Failed to load Google Maps script.");
        };

        document.head.appendChild(script);
    };

    const handleAddressInputChange = async (newValue) => {
        setAddress(newValue);
        
        if (newValue && newValue.length > 2 && autocompleteService) {
            try {
                const predictions = await new Promise((resolve, reject) => {
                    autocompleteService.getPlacePredictions(
                        {
                            input: newValue,
                            componentRestrictions: { country: 'us' },
                            types: ['address']
                        },
                        (results, status) => {
                            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                                resolve(results);
                            } else {
                                reject(status);
                            }
                        }
                    );
                });
                setAddressPredictions(predictions);
            } catch (error) {
                console.error('Error fetching address predictions:', error);
                setAddressPredictions([]);
            }
        } else {
            setAddressPredictions([]);
        }
    };

    const handleAddressSelect = async (event, prediction) => {
        if (prediction && placesService) {
            try {
                const placeDetails = await new Promise((resolve, reject) => {
                    placesService.getDetails(
                        { placeId: prediction.place_id },
                        (result, status) => {
                            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                                resolve(result);
                            } else {
                                reject(status);
                            }
                        }
                    );
                });

                // Extract address components
                let streetNumber = '';
                let route = '';
                let newCity = '';
                let newState = '';
                let newZipCode = '';

                placeDetails.address_components.forEach(component => {
                    const type = component.types[0];
                    switch (type) {
                        case 'street_number':
                            streetNumber = component.long_name;
                            break;
                        case 'route':
                            route = component.long_name;
                            break;
                        case 'locality':
                            newCity = component.long_name;
                            break;
                        case 'administrative_area_level_1':
                            newState = component.short_name;
                            break;
                        case 'postal_code':
                            newZipCode = component.long_name;
                            break;
                        default:
                            break;
                    }
                });

                setAddress(`${streetNumber} ${route}`.trim());
                setCity(newCity);
                setState(newState);
                setZipCode(newZipCode);
            } catch (error) {
                console.error('Error fetching place details:', error);
            }
        }
    };

    return {
        address,
        city,
        state,
        zipCode,
        addressPredictions,
        handleAddressInputChange,
        handleAddressSelect,
        setAddress,
        setCity,
        setState,
        setZipCode
    };
};
